// Every endpoint the Workstation reads, in one place.
//
// WHY ONE FILE
// ============
// Two tabs tonight and six to come, and the audit
// (`design/system/audits/workstation-tabs.md`) names a specific endpoint for
// every one of them. Scattering the calls through the panels that render them
// would make "which endpoints does the Workstation touch" a question answered
// by grep rather than by reading one list — and the answer matters, because R4
// says no new endpoint may be added and the audit is the evidence that none was.
//
// EVERY ROUTE HERE ALREADY EXISTS AND IS CITED
// ============================================
// Each function carries the `file:line` the audit recorded it at. Nothing here
// is composed, aggregated or invented on the backend: these are GETs against
// live, mounted, unflagged routes, plus the two POSTs the existing creative
// actions already make.
//
// FAILURES RETURN, THEY DO NOT THROW
// ==================================
// Every call resolves to `{ ok, data, error }`. A panel that cannot read its
// endpoint must render the designed error or empty state, not take the tab down
// — and a tab of six panels where one endpoint is slow or founder-gated is the
// ordinary case, not the exception. `/decisions/quality-summary` is
// `require_founder`, so a non-founder gets a 403 there and a rendered refusal
// rather than a blank screen.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** One request. Never throws; the caller renders what came back. */
async function get(path, params) {
  try {
    const res = await axios.get(`${API}${path}`, {
      params,
      withCredentials: true,
    });
    return { ok: true, data: res.data, error: null };
  } catch (e) {
    return {
      ok: false,
      data: null,
      status: e?.response?.status ?? null,
      error: e?.response?.data?.detail || e?.message || "request failed",
    };
  }
}

/** Same contract as `get` and `post`: never throws, always `{ ok, data, error }`. */
async function patch(path, body) {
  try {
    const res = await axios.patch(`${API}${path}`, body, {
      withCredentials: true,
    });
    return { ok: true, data: res.data, error: null };
  } catch (e) {
    return {
      ok: false,
      data: null,
      status: e?.response?.status ?? null,
      error: e?.response?.data?.detail || e?.message || "request failed",
    };
  }
}

async function post(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, {
      withCredentials: true,
    });
    return { ok: true, data: res.data, error: null };
  } catch (e) {
    return {
      ok: false,
      data: null,
      status: e?.response?.status ?? null,
      error: e?.response?.data?.detail || e?.message || "request failed",
    };
  }
}

/** Same contract as the others: never throws, always `{ ok, data, error }`.
 *
 * The STATUS matters here and is already carried above: session deletion
 * answers 404 (gone), 403 (not yours) and 409 (it is one track of a set) as
 * three different refusals, and the screen says something different for each.
 */
async function del(path, params) {
  try {
    const res = await axios.delete(`${API}${path}`, {
      params,
      withCredentials: true,
    });
    return { ok: true, data: res.data, error: null };
  } catch (e) {
    return {
      ok: false,
      data: null,
      status: e?.response?.status ?? null,
      error: e?.response?.data?.detail || e?.message || "request failed",
    };
  }
}

// ── offers ─────────────────────────────────────────────────────────────────

/** The picker. `offers.py:468` */
export const listOffers = (params) => get("/offers", { limit: 200, ...params });

/** One offer's identity and context strip. `offers.py:609` */
export const getOffer = (offerId) => get(`/offers/${offerId}`);

// ── Overview · the KPI layer ───────────────────────────────────────────────

/** Offer-scoped snapshots — the KPI layer's source. `performance.py:552` */
export const getSnapshots = (offerId, params) =>
  get("/performance/snapshots", { offer_id: offerId, limit: 500, ...params });

/** Campaign rollup; each row carries `offer_id`. `performance.py:596` */
export const listCampaigns = () => get("/performance/campaigns");

/** Offer-scoped P&L. `performance.py:1063` */
export const getPnl = (offerId, params) =>
  get("/performance/pnl", { offer_id: offerId, limit: 200, ...params });

/**
 * "Cipher's Understanding" — the five-category synthesis for the dominant
 * Overview panel. `offers.py:616`, composed by
 * `services/offer_understanding.py`. Returns
 * `{ offer_id, offer_name, business_unit_id, vertical, generated_at,
 *    has_data, item_count, working, not_working, changed_meaningfully,
 *    learned, noteworthy }` — the five category keys are always arrays,
 * empty rather than fabricated when there is nothing to say.
 */
// ── Research pack (RESEARCH-PACK-BUILD, 2026-09-09) ────────────────────────
// The old Research tab read a Ghost Recon intelligence brief whose five backing
// collections hold 0 documents in production, so every panel rendered empty.
// These three call the research engine that already existed and had never been
// run: `offer_research` held 0 records and nothing in the front end called it.

// `include_items=true` because the screen shows the findings themselves, not
// counts. The summary shape exists for lists; this is a detail view.
export const getOfferResearch = (offerId) =>
  get(`/offers/${offerId}/research`, { include_items: true });

// One click = the whole pack. Sequential on the server (they share one browser
// pod), so this is slow by design — the caller must show that it is working.
export const runOfferResearchPack = (offerId, body) =>
  post(`/offers/${offerId}/research/run-pack`, body || {});

// Re-run exactly one source, for when a single source failed and the rest are
// still good. Spends credits, hence its own explicit call.
export const regenerateOfferResearchSource = (offerId, source, limit) =>
  post(`/offers/${offerId}/research`, { source, ...(limit ? { limit } : {}) });

// Audience — first-party performance from our own spend (WORKSTATION_AUDIENCE.md).
// Separate from Research by Founder ruling: Research is external market
// intelligence, Audience is what our own ads actually did.
export const getOfferAudience = (offerId) =>
  get(`/offers/${offerId}/audience`);

export const getOfferUnderstanding = (offerId) =>
  get(`/offers/${offerId}/understanding`);

// ── Analytics · Overall ────────────────────────────────────────────────────

/** Cross-campaign summary. `performance.py:757` */
export const getPerformanceSummary = () => get("/performance/summary");

/** P&L aggregates. `performance.py:1094` */
export const getPnlAggregates = (groupBy = "campaign") =>
  get("/performance/pnl/aggregates", { group_by: groupBy });

/** Predictions summary. `predictions.py:110` */
export const getPredictionsSummary = () => get("/predictions/summary");

/** Founder-only. `predictions.py:203` — a 403 here is correct, not a bug. */
export const getDecisionQuality = () => get("/decisions/quality-summary");

/** Campaigns rising on every cost metric. `performance.py:997` */
export const getAtRisk = (offerId) =>
  get("/performance/campaigns/at-risk", { offer_id: offerId });

/** Campaigns worth more budget. `performance.py:1022` */
export const getScaleCandidates = (offerId) =>
  get("/performance/campaigns/scale-candidates", { offer_id: offerId });

// ── Opportunities ──────────────────────────────────────────────────────────
//
// Same two list calls Analytics · Overall already uses above (`getAtRisk`,
// `getScaleCandidates`), plus the per-campaign diagnosis this tab adds when
// an operator selects a flagged campaign, and the specialist's assessment of
// it. All read `campaign_health_status`.
//
// CORRECTED 2026-09-09 (OFFER-PERFORMANCE-DEPARTMENT): the note that used to
// stand here — "no scheduler job calls it, so an empty result is the expected
// first-load state" — was true when written and is no longer. The sweep now
// runs daily at 06:00 UTC (`schedulers.py`, job `campaign_health_sweep`), so
// an empty list is no longer self-explanatory: it means "nothing flagged as of
// the last sweep" only if a sweep actually completed. Both list responses
// therefore carry a `sweep` object, and the screen must read it before
// describing an empty list. See `getLastHealthSweep` below.

/** Failure-mode diagnosis for one flagged campaign. `performance.py:1469` */
export const getCampaignDiagnosis = (campaignId) =>
  get(`/performance/campaigns/diagnosis/${campaignId}`);

/**
 * When the health sweep last ran, and what it found.
 *
 * The two list endpoints already embed this as `sweep`; this standalone call
 * is for a caller that needs the sweep state without a campaign list. Returns
 * `{ran: false}` when no sweep has ever run, `{ran: null, error}` when the
 * lookup itself failed, and the sweep record otherwise — three states, kept
 * distinct on purpose.
 */
export const getLastHealthSweep = () => get("/performance/health/last-sweep");

/**
 * The most recent specialist assessment declared against one campaign.
 *
 * Advisory. This is an agent's recommendation, never an approval or a launch
 * green light, and nothing behind it moves a budget or a bid. `state` is
 * `available`, `none` (nobody has assessed it yet) or `unavailable` (the
 * lookup failed) — the last two must not render the same way.
 */
export const getCampaignAssessment = (campaignId) =>
  get(`/performance/campaigns/${campaignId}/assessment`);

// ── Analytics · Individual ─────────────────────────────────────────────────

/**
 * Creative-grain engagement: CTR, CPM, watch time, thumb-stop, hook retention,
 * joined to spend/CPA/ROAS. `performance.py:816`
 *
 * This is CREATIVE grain, not AD grain. Ad-grain rankings (Top/Worst
 * Performers, Most Profitable, Highest CTR, Lowest CPC) are backed
 * separately by `getAdPerformance` below, reading
 * `/offers/{offer_id}/ads/performance` (R4, closed).
 */
export const getCreativeEngagement = (params) =>
  get("/performance/creative-engagement", { limit: 100, ...params });

/**
 * Ad-grain performance, ranked — merges `ad_performance_snapshots` (Meta
 * engagement) with `redtrack_ad_performance_snapshots` (RedTrack
 * spend/revenue/conversions), scoped to one offer. `performance.py:1273`.
 * Built specifically to back the Individual-tab per-ad rankings
 * (Top/Worst Performers, Most Profitable, Highest CTR, Lowest CPC) — R4,
 * closed.
 */
export const getAdPerformance = (offerId, params) =>
  get(`/offers/${offerId}/ads/performance`, { sort_by: "spend", limit: 50, ...params });

/** Side-by-side across campaigns. `performance.py:863` */
export const getCreativeComparison = (campaignIds) =>
  get("/performance/creative-comparison", {
    campaign_ids: (campaignIds || []).join(","),
  });

// ── Research & Audience ─────────────────────────────────────────────────────
//
// Two-hop, per the audit (§3): an offer only has a dossier when it carries
// `ghost_recon_offer_id` (`models.py:207`), set when the offer was created
// from a Ghost Recon offer (`ghost_recon.py:300` dedupes on it). Offers
// created via `/offers/manual` or plain `POST /offers` have none, and the
// tab legitimately renders empty for them — that is correct, not a bug.
//
// The intelligence brief is self-contained: it already embeds
// `velocity_history` server-side (`ghost_recon.py:176`), so this file does
// NOT call a separate `/velocity` endpoint — that route was removed
// (`ghost_recon.py:7`) and the one existing consumer that still calls it,
// `GhostReconOfferDetail.jsx`, is broken because of exactly that (audit §3b).

/** The dossier record itself. `ghost_recon.py:97` */
export const getGhostReconOffer = (grOfferId) =>
  get(`/ghost-recon/offers/${grOfferId}`);

/** The full brief — offer, funnel, comments, creative families, competitors,
 *  velocity — composed server-side in one call. `ghost_recon.py:161` */
export const getIntelligenceBrief = (grOfferId) =>
  get(`/ghost-recon/offers/${grOfferId}/intelligence-brief`);

// ── Landing Pages ────────────────────────────────────────────────────────────

/** Structured LP variants configured on the offer. `creative_engine.py:2252` */
export const getLandingPageVariants = (offerId) =>
  get(`/offers/${offerId}/landing-page-variants`);

/**
 * Per-variant performance + winner declaration, across both business units.
 * Offer-grouped response — the Workstation filters client-side by the
 * selected offer. `creative_engine.py:2304`, `services/winner_rotator.py`.
 */
export const getWinnerRotator = (params) =>
  get("/winner-rotator", params);

// ── Ads & Creative ───────────────────────────────────────────────────────────

/** Every production for the offer. `creative_engine.py:709` */
export const getOfferProductions = (offerId) =>
  get(`/offers/${offerId}/productions`);

/**
 * Mark a production complete on finished-video return, attaching the finished
 * video's Google Drive file URL (issue #108). This is the single point a
 * video/autonomous creative registers. POST against the EXISTING route
 * `ops.py:mark_production_complete` — no new endpoint is added.
 */
export const markProductionComplete = (productionId, outputUrl) =>
  post(`/productions/${productionId}/mark-complete`, { output_url: outputUrl });

/** Engine-wide stats (not offer-scoped). `creative_engine.py:1947` */
export const getCreativeEngineStats = () => get("/creative-engine/stats");

// ── Custom Creative Sessions (V2-CREATIVE-WAVE1) ─────────────────────────────
//
// `GET /ccs/sessions` is NEW — the one exception to "no new endpoint" on this
// surface, and it exists because the Founder ruled it on 2026-09-08. There had
// never been a list endpoint: 13 CCS routes and not one of them a list, so 34
// of 37 live sessions could not be reached again by any surface once the
// operator navigated away — including sessions v2's own Custom button had just
// created. `custom_creative_session.py`, registered above the by-id route.
//
// The rest of the CCS calls are NOT re-expressed here. They live in
// `components/custom_creative_session/useCcsApi.js`, which is the single hook
// every v1 CCS component already goes through, and v2 imports it rather than
// growing a second copy that can drift. That hook is the reason this whole
// screen is a restyle.

/** This operator's Custom sessions, newest first. `custom_creative_session.py` */
export const listCustomSessions = (params) => get("/ccs/sessions", params);

// ── The Autonomous wizard (V2-AUTONOMOUS-WAVE2B) ─────────────────────────────
// Every route here already exists and is already called by v1's wizard — see
// `V2_AUTONOMOUS_SCOPE.md` §1, which enumerated all 39. Nothing new is added on
// the backend; this is the same surface reached from a v2 screen.

// `getOffer` already exists above — reused rather than redeclared.

/** One Autonomous session, including its stored `current_step`. */
export const getCreativeSession = (sessionId) =>
  get(`/creative/sessions/${sessionId}`);

/** Record the step the operator is on. The server allowlists these fields. */
export const setCreativeSessionStep = (sessionId, step, label) =>
  patch(`/creative/sessions/${sessionId}`, { current_step: step, step_label: label });

/** Delete a creative session. Hard and immediate — Founder ruling 2026-09-09.
 *
 * `cascade` is the explicit "delete the whole multi-angle set" action. Without
 * it, deleting one track of a set is REFUSED with a 409 that explains why,
 * rather than quietly leaving a set that says three and has two.
 *
 * The session's angles, hooks, scripts and CTAs belong to the OFFER and are not
 * touched. `creative_engine.py` */
export const deleteCreativeSession = (sessionId, { cascade = false } = {}) =>
  del(`/creative/sessions/${sessionId}`, cascade ? { cascade: true } : undefined);

/** Record this session's video format / creative type (step 1's question).
 *
 * A NAMED function rather than reaching for the module-local `patch` through
 * the namespace — `api.patch` is not exported, so calling it would have been
 * `undefined is not a function` at the first change of a dropdown. Caught
 * before it shipped, but only because the export list was checked. */
export const setCreativeSessionMeta = (sessionId, body) =>
  patch(`/creative/sessions/${sessionId}`, body);

/** Bind this session to its angle, and open a sibling per remaining angle.
 *
 * MA-1's endpoint; MA-3 is what finally calls it. The session the operator is
 * already in becomes track 1 — it holds the psychology map the whole set is
 * built on. `creative_engine.py` */
export const createSessionTracks = (sessionId, angleIds) =>
  post(`/creative/sessions/${sessionId}/tracks`, { angle_ids: angleIds });

/** This session's track set — itself and its siblings, each with its angle.
 *
 * A session that is not part of a set answers `track_total: 1`, which is a real
 * answer: the shell must be able to tell "one track" from "could not read the
 * tracks". */
export const getSessionTracks = (sessionId) =>
  get(`/creative/sessions/${sessionId}/tracks`);

/** What the daily expiry sweep would do right now. Reads only, never writes. */
export const getSessionSweepPreview = () => get("/creative/sessions/sweep-preview");

/** The offer's scripts, and the approved-but-unrouted subset. */
export const getOfferScripts = (offerId) => get(`/offers/${offerId}/scripts`);
export const getApprovedUnroutedScripts = (offerId) =>
  get(`/offers/${offerId}/scripts/approved-unrouted`);

/** The offer's hooks. `selected` marks the ones chosen for scripts. */
export const getOfferHooks = (offerId) => get(`/offers/${offerId}/hooks`);

// ── Traffic Channels ─────────────────────────────────────────────────────────
//
// No channel-mix aggregation endpoint exists (audit §6) — Founder-parked as
// new business-logic-free composition. Built here client-side from two real
// calls already used elsewhere on the Workstation: campaigns carry
// `channel` (required field, `models.py:274`), snapshots carry
// `traffic_source` / `affiliate_network`.

/** Campaigns for the offer, each carrying `channel`. `campaigns.py:508`
 *
 *  THIS USED TO CALL A ROUTE THAT DOES NOT EXIST (#220). It requested
 *  `GET /api/offers/{offer_id}/campaigns` and cited `creative_engine.py:1346`;
 *  no such route is mounted, and that citation is
 *  `POST /scripts/{script_id}/route-to-production`, unrelated. The call could
 *  only ever 404, so Channel mix has never had campaign data.
 *
 *  `GET /api/campaigns` is the real read, and it is account-wide — its only
 *  filters are `business_unit_id`, `operator_id` and `status`, so there is no
 *  `offer_id` to pass. The offer scoping therefore happens here, on
 *  `Campaign.offer_id` (required, `models.py:352`). That is the same
 *  client-side composition this section already commits to above, not a new
 *  backend concern, so no route was added.
 *
 *  Returns a bare array, because `GET /api/campaigns` returns
 *  `List[Campaign]`. The `{ ok, data, error }` envelope is preserved. */
export const getOfferCampaigns = async (offerId) => {
  const res = await get("/campaigns");
  if (!res.ok) return res;
  const all = Array.isArray(res.data) ? res.data : [];
  return { ...res, data: all.filter((c) => c?.offer_id === offerId) };
};

// ── Learnings ─────────────────────────────────────────────────────────────────
//
// Not offer-scoped (audit §7) — `_intelligence_list` reads each `cipher_*`
// collection with a literal empty query across both BU databases. Rendered
// account-wide, not filtered to the selected offer; the tab says so rather
// than implying a scope it does not have. All three are `require_founder`.

/** `performance.py:286` */
export const getIntelligenceExperiments = () => get("/intelligence/experiments");
/** `performance.py:292` */
export const getIntelligenceObservations = () => get("/intelligence/observations");
/** `performance.py:298` */
export const getIntelligenceHypotheses = () => get("/intelligence/hypotheses");

/** Winning/failed creative patterns, ranked by CPA. Not founder-gated.
 *  `predictions.py:493` */
export const getTopPatterns = (params) =>
  get("/creative-intelligence/top-patterns", params);

/** Fatigue benchmarks per pattern. Not founder-gated. `predictions.py:505` */
export const getFatigueBenchmarks = (params) =>
  get("/creative-intelligence/fatigue-benchmarks", params);

// ── the two creative actions ───────────────────────────────────────────────
//
// These reach the SAME handlers `CreativeEngineDropdown` (`App.js`) and
// `CreativeHub` reach, including the `REACT_APP_CCS_ENABLED` cutover branch.
//
// UPDATE (I6, creative-engine restructure, 2026-08-18): this used to
// re-express the call sequence locally because `App.js` was "frozen" for an
// earlier dispatch and the component wasn't exported. That constraint was
// scoped to that one night, not permanent, and I6's whole job is to collapse
// this exact duplication (a third copy in `CreativeHub.jsx` had already
// drifted and dropped the CCS branch entirely). All three entry points now
// call the single implementation in `frontend/src/services/creativeSessionStart.js` —
// this file just re-exports it so existing imports of `./api` keep working.
export {
  listCreativeSessions,
  ccsEnabled,
  startCreativeSession,
  // CREATIVE-SESSION-UX-FIXES item 1: v2 had ONE "New Creative Session" button
  // whose behaviour depended on REACT_APP_CCS_ENABLED — live value `true`, read
  // from Render on 2026-09-08 — so v2 could not start an Autonomous session by
  // any route. The chooser existed only in v1 (`App.js`'s CreativeLanding).
  // This handler is the autonomous half and deliberately never consults the
  // flag; see its own docstring for why that separation exists.
  startAutonomousCreativeSession,
  sessionPath,
} from "../../../services/creativeSessionStart";

// Compliance review — ABH creative, Specialist-run. Advisory, never a gate.
export const getComplianceReview = (offerId) =>
  get(`/offers/${offerId}/compliance-review`);
export const runComplianceReview = (scriptId) =>
  post(`/scripts/${scriptId}/compliance-review`, {});
